// The ambient soundtrack: a shuffled playlist of the six tranquil tracks, played
// through HTMLAudioElements (they stream mp3 without a full WebAudio decode) and
// crossfaded with gsap volume tweens. Auto-advances on `ended`.
//
// Depends one-directionally on audio.js (the prefs store + the first-gesture
// unlock); audio.js never imports back. main.js pulls this in with a side-effect
// import. Defaults to ON: it fades the first track in at the user's first gesture
// (the earliest moment browsers allow audio), and reacts live to the drawer's
// music enable/volume/mute changes.

import { onFirstGesture, onMusicSettingChange, onVisibilityChange, getPrefs } from './audio.js';

// [file, display name]. Order here is the source; playback order is shuffled.
const TRACKS = [
  ['assets/sounds/bright-halo-tranquilium.mp3', 'Bright Halo'],
  ['assets/sounds/cloudette-tranquilium.mp3', 'Cloudette'],
  ['assets/sounds/integration-be-still-the-earth-sloer-all-ambient.mp3', 'Integration'],
  ['assets/sounds/pertranseo-slower-headspace-all-ambient.mp3', 'Pertranseo'],
  ['assets/sounds/renew-tranquilium.mp3', 'Renew'],
  ['assets/sounds/soma-be-still-the-earth-we-dream-of-eden-all-ambient-main-version-42606-03-36.mp3', 'We Dream of Eden'],
];

const FADE = 2.5; // crossfade / fade-in-out seconds

// Fisher–Yates on the indices (cosmetic — uses Math.random, never the seed).
const order = TRACKS.map((_, i) => i);
for (let i = order.length - 1; i > 0; i--) {
  const j = Math.floor(Math.random() * (i + 1));
  [order[i], order[j]] = [order[j], order[i]];
}

let pos = 0;          // cursor into `order`
let current = null;   // the HTMLAudioElement currently fading up / playing
let started = false;  // has the first track ever been kicked off

// Target element volume = base(1) × musicVolume × !muted. Read live from prefs.
function targetVolume() {
  const p = getPrefs();
  return p.muted ? 0 : p.musicVolume;
}

function setNowPlaying(name) {
  const el = document.getElementById('nowPlaying');
  if (el) el.textContent = name;
}

// Build a fresh <audio> for one track. New element per track keeps crossfades
// trivial (fade the old one out, the new one in, then drop the old one).
function makeAudio(trackIndex) {
  const [file, name] = TRACKS[order[trackIndex] % TRACKS.length];
  const a = new Audio(file);
  a.preload = 'auto';
  a.volume = 0;
  a._trackName = name;
  a.addEventListener('ended', () => { if (a === current) next(); });
  return a;
}

// Fade `el` out over FADE then stop+release it.
function fadeOutAndStop(el) {
  if (!el) return;
  gsap.to(el, {
    volume: 0,
    duration: FADE,
    onComplete: () => {
      el.pause();
      el.src = '';
    },
  });
}

// Start playing the track at `pos`, fading it in. Crossfades out whatever was
// playing. No-op if music is disabled.
function playCurrent() {
  if (!getPrefs().musicEnabled) return;
  const prev = current;
  const a = makeAudio(pos);
  current = a;
  started = true;
  setNowPlaying(a._trackName);
  a.play().then(() => {
    gsap.to(a, { volume: targetVolume(), duration: FADE });
  }).catch(() => {
    /* play() can reject if we somehow beat the gesture unlock — first gesture
       will retry via the onFirstGesture handler below */
  });
  fadeOutAndStop(prev);
}

// Advance to the next track in the shuffled order (wrapping).
function next() {
  pos = (pos + 1) % order.length;
  playCurrent();
}
window.nextTrack = next;

// Stop everything (fade out). Used when music is toggled off.
function stop() {
  fadeOutAndStop(current);
  current = null;
  started = false;
}

// Fade the first track in at the first user gesture (if music is enabled).
onFirstGesture(() => {
  if (getPrefs().musicEnabled) playCurrent();
});

// Pause the music while the tab is hidden, resume it on return (a backgrounded
// tab is expected to go quiet). pause()/play() preserve the playback position;
// the gsap fade volume is untouched, so it picks up exactly where it left off.
onVisibilityChange((hidden) => {
  if (!current) return;
  if (hidden) current.pause();
  else if (getPrefs().musicEnabled) current.play().catch(() => { /* will retry on next gesture */ });
});

// React live to the drawer: enable/disable starts or stops playback; volume/mute
// retarget the current fade.
onMusicSettingChange((p) => {
  if (!p.musicEnabled) {
    if (started) stop();
    return;
  }
  if (!started) {
    playCurrent(); // turned on — start (we're necessarily past the first gesture)
  } else if (current) {
    gsap.to(current, { volume: targetVolume(), duration: 0.4 });
  }
});
